import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { NavigateOptions, To, useBlocker, useLocation, useNavigate } from "react-router-dom";
import { locationCheck } from "./unsaved-changes/locationCheck";

interface UnsavedChangesContextType {
  clearUnsavedChanges: () => void;
  hasUnsavedChanges: boolean;
  isLoading: string | null;
  navigate: (url: To, id: string, options?: NavigateOptions) => void;
  navigateAfterSave: (url: To, id: string, options?: NavigateOptions) => void;
  registerUnsavedChanges: (id: string, hasChanges: boolean) => void;
}

export const defaultUnsavedChangesContext: UnsavedChangesContextType = {
  clearUnsavedChanges:
    // istanbul ignore next
    () => {},
  hasUnsavedChanges: false,
  isLoading: null,
  /**
   * Fallback navigate implementation used when the provider is not available.
   * This performs a hard navigation by setting `window.location.href`.
   */
  navigate: () => {
    window.location.href = "/";
  },
  /**
   * Fallback navigate-after-save used when the provider is not mounted.
   * Intended to be no-op in test environments; real behaviour is provided
   * by the `UnsavedChangesProvider` implementation.
   */
  navigateAfterSave:
    // istanbul ignore next
    () => {},
  /**
   * Fallback registerUnsavedChanges used when the provider is not mounted.
   */
  registerUnsavedChanges:
    // istanbul ignore next
    () => {},
};

export const UnsavedChangesContext = createContext<UnsavedChangesContextType>(
  defaultUnsavedChangesContext,
);

/**
 * Provides unsaved changes tracking and navigation control to child components.
 *
 * @param params - The component props
 * @param params.children - The child components to render within the provider
 */
export const UnsavedChangesProvider = ({ children }: { children: ReactNode }) => {
  const [_isPending, startTransition] = useTransition();
  const rrNavigate = useNavigate();
  const [unsavedChangesSources, setUnsavedChangesSources] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<string | null>(null);

  useEffect(() => {
    startTransition(() => setIsLoading(null));
  }, [location.pathname]);

  const clearUnsavedChanges = useCallback(() => {
    setUnsavedChangesSources({});
  }, []);

  /**
   * Clear all registered unsaved-change flags.
   * Used after a successful save or when navigation is forced.
   */
  const registerUnsavedChanges = useCallback((id: string, hasChanges: boolean) => {
    setUnsavedChangesSources((prev) => ({
      ...prev,
      [id]: hasChanges,
    }));
  }, []);

  /**
   * Register or update a component's unsaved-changes state.
   *
   * @param id - The identifier for the component registering changes
   * @param hasChanges - True if the component currently has unsaved changes
   */
  const hasUnsavedChanges = useMemo(
    () => Object.values(unsavedChangesSources).some(Boolean),
    [unsavedChangesSources],
  );

  const navigate = useCallback(
    (url: To, id: string, options?: NavigateOptions) => {
      setIsLoading(id);
      rrNavigate(url, options);
    },
    [rrNavigate],
  );

  /**
   * Trigger navigation and set the component `id` as the loading source.
   * This prevents the unsaved-changes blocker from prompting while
   * navigation initiated by the same component proceeds.
   *
   * @param url - The destination to navigate to
   * @param id - The component id initiating navigation
   * @param options - Optional navigation options forwarded to the router
   */
  const navigateAfterSave = useCallback(
    (url: To, id: string, options?: NavigateOptions) => {
      setIsLoading(id);
      clearUnsavedChanges();
      setTimeout(() => {
        rrNavigate(url, options);
      }, 1);
    },
    [clearUnsavedChanges, rrNavigate],
  );

  /**
   * Navigate after performing a save: clear any unsaved flags and then
   * navigate to the supplied `url`. `setTimeout` is used to allow any
   * save completion logic to settle before routing.
   *
   * @param url - The destination to navigate to after save
   * @param id - The component id initiating the save-and-navigate flow
   * @param options - Optional navigation options forwarded to the router
   */

  // Single beforeunload handler
  useEffect(() => {
    /**
     * Beforeunload handler that prompts the browser to show a confirmation
     * if there are registered unsaved changes.
     *
     * @param event - The beforeunload event provided by the browser
     */
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      /* istanbul ignore else */
      if (hasUnsavedChanges) {
        event.preventDefault();
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Register the blocker with the router
  useBlocker((args) => {
    if (!hasUnsavedChanges) return false;
    const { currentLocation, nextLocation } = args;
    if (locationCheck(currentLocation.pathname, nextLocation.pathname)) {
      const question = window.confirm(
        "You have unsaved changes. Do you want to discard them and navigate away?",
      );
      // Clear loading flag if you don't want to navigate
      if (!question) setIsLoading(null);
      return !question;
    }

    return false;
  });

  return (
    <UnsavedChangesContext.Provider
      value={{
        clearUnsavedChanges,
        hasUnsavedChanges,
        isLoading,
        navigate,
        navigateAfterSave,
        registerUnsavedChanges,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
};
UnsavedChangesProvider.displayName = "UnsavedChangesProvider";

/**
 * Hook-specific helper that scopes navigation helpers to a component id.
 *
 * @param id - Identifier for the component using this hook (used to scope loading state).
 */
export const useUnsavedChanges = (id: string) => {
  const cxt = useContext(UnsavedChangesContext);
  return {
    ...cxt,
    /**
     * Navigate while scoping the call to this hook's `id` so that the
     * provider can mark which component initiated loading.
     *
     * @param url - The destination to navigate to
     * @param options - Optional navigate options forwarded to the router
     */
    navigate: (url: To, options?: NavigateOptions) => {
      cxt.navigate(url, id, options);
    },
    /**
     * Navigate after a successful save, clearing this component's
     * unsaved-changes state before performing the navigation.
     *
     * @param url - The destination to navigate to after save
     * @param options - Optional navigate options forwarded to the router
     */
    navigateAfterSave: (url: To, options?: NavigateOptions) => {
      cxt.navigateAfterSave(url, id, options);
    },
  };
};
